import { Box, Text, IconButton, FormLabel, Divider, Flex, Spinner, SimpleGrid, Icon, Heading } from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconArrowRight, IconClose } from 'assets/icons';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import Title from 'components/Title';
import { useFetchADPPTOCategories } from 'hooks/integrations/adp/useFetchADPPTOCategories';
import {
  IMapUnmapADPCodesToPTOCategory,
  useMapUnmapADPCodesToPTOCategories,
} from 'hooks/integrations/adp/useMapUnmapADPCodeToPTOCategories';
import { fetchADPPaginatedCompany } from 'hooks/integrations/useFetchPaginatedADPCompanies';
import { fetchADPPaginatedEarningCodes } from 'hooks/integrations/useFetchPaginatedADPEarningCodes';
import { useFetchADPSelectedEarningCodes } from 'hooks/integrations/useFetchSelectedEarningCodes';
import { useMapADPEarningCodes, IADPMapEarningCodes } from 'hooks/integrations/useMapADPEarningCodes';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import CompanyCodeBlock from './CompanyCodeBlock';

const CodesMapping = () => {
  const { t } = useTranslation(['integrations']);

  const [currentMap, setCurrentMap] = useState<string>('');
  const [companyId, setCompanyId] = useState<any>('');

  const { status: earningCodeMapStatus, mutateAsync: earningCodeMapMutateAsync } = useMapADPEarningCodes();
  const { status: mapUnmapStatus, mutateAsync: mapUnmapADPCodesToPTOCategories } = useMapUnmapADPCodesToPTOCategories();
  const {
    status: selectedEarningCodeStatus,
    data: selectedEarningCodeData,
    refetch,
  } = useFetchADPSelectedEarningCodes(companyId);
  const { data: adpPTOcategoriesData, refetch: refetchPTOCategories } = useFetchADPPTOCategories(companyId);

  const companiesLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchADPPaginatedCompany, { page, search });
  };

  const earningCodesLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchADPPaginatedEarningCodes, { page, search, adpCompanyCode: companyId });
  };

  const onCompanySelected = (companyId: any) => {
    setCompanyId(companyId?.adpCompanyCode);
  };

  const onEarningCodeSelected = (earningCode: any, type: string) => {
    setCurrentMap(type);
    const postData: IADPMapEarningCodes = {
      adp_company_code: companyId,
      code_value: earningCode?.codeValue,
      type: type,
      earning_code_id: earningCode?.id,
    };
    earningCodeMapMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const handlePTOEarningCodeChange = (earningCode: any, category: any, mapping: boolean = true) => {
    setCurrentMap(category.id);
    const postData: IMapUnmapADPCodesToPTOCategory = {
      mapping: mapping,
      adp_company_code: companyId,
      adp: [{ time_off_category_id: category?.id, adp_earning_code_id: earningCode?.id }],
    };
    mapUnmapADPCodesToPTOCategories(postData).then(() => {
      refetchPTOCategories();
      refetch();
    });
  };

  return (
    <Box>
      <Title>{t('integrations:adp.adpWfnEarningsCode')}</Title>
      <Box mt={8} maxWidth="300px">
        <FormLabel>{t('integrations:adp.adpCompanyCode')}</FormLabel>
        <ReactSelectAsyncPaginate
          loadOptions={companiesLoadOptions}
          additional={{
            page: 1,
          }}
          onChange={(value: any) => {
            onCompanySelected(value);
          }}
          size="md"
          variant="filled"
          fontSize="sm"
          width="100%"
          debounceTimeout={300}
          menuPortalTarget={document.body}
          isSearchable
          placeholder="Select a company"
          getOptionLabel={(option: any) => option.adpCompanyCode}
          getOptionValue={(option: any) => option.adpCompanyCode}
        />
      </Box>
      <Divider mt={6} mb={3} />
      <SimpleGrid columns={{ base: 1, '2xl': 2 }} spacing="4" position="relative" justifyItems="center">
        <Box width={{ base: '100%', md: 'auto' }}>
          {companyId && selectedEarningCodeStatus === 'success' ? (
            <>
              <Heading as="h6" size="sm" my="6">
                Timeero Earning Codes Mapping
              </Heading>
              <Flex
                gap="2"
                alignItems="center"
                justifyContent="space-between"
                maxWidth="550px"
                color="gray.600"
                mb="4"
                display={{ base: 'none', md: 'flex' }}
              >
                <Text fontWeight="semibold">Timeero Earning Codes</Text>
                <Text flex="0 0 250px" fontWeight="semibold">
                  ADP Earning Codes
                </Text>
              </Flex>
              {/* Regular Earning Code */}
              <CompanyCodeBlock
                companyCodeLabel={t('integrations:adp.regularEarningCode')}
                currentMap={currentMap}
                earningCodeMapStatus={earningCodeMapStatus}
                onEarningCodeSelected={onEarningCodeSelected}
                earningCodesLoadOptions={earningCodesLoadOptions}
                selectedEarningCodeData={selectedEarningCodeData}
                placeholder="Regular earning code"
                companyCode="regular"
              />

              {/* Overtime Earning Code */}
              <CompanyCodeBlock
                companyCodeLabel={t('integrations:adp.overtimeEarningCode')}
                currentMap={currentMap}
                earningCodeMapStatus={earningCodeMapStatus}
                onEarningCodeSelected={onEarningCodeSelected}
                earningCodesLoadOptions={earningCodesLoadOptions}
                selectedEarningCodeData={selectedEarningCodeData}
                placeholder="Overtime earning code"
                companyCode="overtime"
              />
              <CompanyCodeBlock
                companyCodeLabel={t('integrations:adp.doubleOvertimeEarningCode')}
                currentMap={currentMap}
                earningCodeMapStatus={earningCodeMapStatus}
                onEarningCodeSelected={onEarningCodeSelected}
                earningCodesLoadOptions={earningCodesLoadOptions}
                selectedEarningCodeData={selectedEarningCodeData}
                placeholder="Double overtime earning code"
                companyCode="dovertime"
              />
              {/* Mielage Earning Code */}
              <CompanyCodeBlock
                companyCodeLabel={t('integrations:adp.mileageEarningCode')}
                currentMap={currentMap}
                earningCodeMapStatus={earningCodeMapStatus}
                onEarningCodeSelected={onEarningCodeSelected}
                earningCodesLoadOptions={earningCodesLoadOptions}
                selectedEarningCodeData={selectedEarningCodeData}
                placeholder="Mileage earning code"
                companyCode="mileage"
              />
              {/* Premium Pay Earning Code */}
              <CompanyCodeBlock
                companyCodeLabel={t('integrations:adp.premiumPayEarningCode')}
                currentMap={currentMap}
                earningCodeMapStatus={earningCodeMapStatus}
                onEarningCodeSelected={onEarningCodeSelected}
                earningCodesLoadOptions={earningCodesLoadOptions}
                selectedEarningCodeData={selectedEarningCodeData}
                placeholder="Premium pay earning code"
                companyCode="premium_pay"
              />
              {/* Holiday */}
              <CompanyCodeBlock
                companyCodeLabel={t('common:holiday')}
                currentMap={currentMap}
                earningCodeMapStatus={earningCodeMapStatus}
                onEarningCodeSelected={onEarningCodeSelected}
                earningCodesLoadOptions={earningCodesLoadOptions}
                selectedEarningCodeData={selectedEarningCodeData}
                placeholder="Holiday"
                companyCode="holiday"
              />
            </>
          ) : null}
        </Box>
        <Box width={{ base: '100%', md: 'auto' }}>
          <Divider
            display={{ base: 'none', '2xl': 'block' }}
            orientation="vertical"
            position="absolute"
            left="calc(50% - 2px)"
          />
          {companyId && adpPTOcategoriesData?.payload?.length ? (
            <Box>
              <Heading as="h6" size="sm" my="6">
                PTO Categories Mapping
              </Heading>
              <Flex
                gap="3"
                alignItems="center"
                justifyContent="space-between"
                maxWidth="570px"
                color="gray.600"
                mb="4"
                display={{ base: 'none', md: 'flex' }}
              >
                <Text fontWeight="semibold">PTO Categories</Text>
                <Text flex="0 0 290px" fontWeight="semibold">
                  ADP Earning Codes
                </Text>
              </Flex>
              {adpPTOcategoriesData?.payload.map((category: any) => {
                return (
                  <Flex
                    key={category.id}
                    gap={{ base: '0', md: '2' }}
                    alignItems="center"
                    justifyContent="space-between"
                    width={{ base: '100%', lg: '570px' }}
                    direction={{ base: 'column', md: 'row' }}
                    mb="4"
                  >
                    <Text
                      background={{ base: 'transparent', md: 'primary.50' }}
                      width="100%"
                      height="100%"
                      px={{ base: '1', md: '3' }}
                      py={{ base: '1', md: '2' }}
                    >
                      {category.name}
                    </Text>
                    <Flex
                      width="100%"
                      flex={{ base: '0 0 100%', md: '0 0 320px' }}
                      alignItems="center"
                      gap="2"
                      position="relative"
                    >
                      <Icon
                        display={{ base: 'none', md: 'inline-block' }}
                        as={IconArrowRight}
                        sx={{ '& path': { fill: 'gray.600' } }}
                        mr="2"
                      />
                      {mapUnmapStatus === 'loading' && currentMap === category.id && (
                        <Flex
                          width="100%"
                          height="100%"
                          position="absolute"
                          zIndex={2}
                          justifyContent="center"
                          alignItems="center"
                          background="rgba(255, 255, 255, 0.7)"
                        >
                          <Spinner size="xs" />
                        </Flex>
                      )}
                      <ReactSelectAsyncPaginate
                        loadOptions={earningCodesLoadOptions}
                        value={category?.earning_code || null}
                        additional={{
                          page: 1,
                        }}
                        onChange={(value: any) => {
                          handlePTOEarningCodeChange(value, category);
                        }}
                        variant="filled"
                        fontSize="xs"
                        width="100%"
                        size="md"
                        debounceTimeout={300}
                        menuPortalTarget={document.body}
                        isSearchable
                        isDisabled={mapUnmapStatus === 'loading' && currentMap === category.id}
                        placeholder="Select earning code"
                        getOptionLabel={(option: any) => option.codeDescription}
                        getOptionValue={(option: any) => option.codeValue}
                      />
                      <IconButton
                        variant="outline"
                        borderColor="gray.400"
                        icon={<Icon as={IconClose} sx={{ '& path': { fill: 'primary.900' } }} />}
                        aria-label="Remove"
                        ml="1"
                        size="sm"
                        title="Remove Mapping"
                        isDisabled={mapUnmapStatus === 'loading' && currentMap === category.id}
                        onClick={() => handlePTOEarningCodeChange(category?.earning_code, category, false)}
                      />
                    </Flex>
                  </Flex>
                );
              })}
            </Box>
          ) : null}
        </Box>
      </SimpleGrid>
    </Box>
  );
};
export default CodesMapping;
